// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { IonText } from '@ionic/react'

import {
	Screen,
	ScreenPanoramaContainer as Container,
	ITEM_SUBTYPE_SCREEN_PANORAMA,
	COLLECTION_ITEM_SUBTYPE_PANORAMA,
	MediaItem,
} from 'mytours-read-common'
import AudioPlayerBarComponent from 'mobile_web/modules/audio/components/AudioPlayerBar'
import {
	linksData,
	onLinkAction,
	getSectionSubtypeAndId,
	getSocialLink,
	getSocialLinkTitle,
	renderScreenRedirect,
} from '../functions'
import { SectionItemType, Hotspot } from '../types'

import Page from '../../common/components/Page'

import {
	FileRec,
	GetCollectionItemsIndexResponseData,
	GetScreensShowResponse,
	GetScreensShowResponseSectionItems,
} from 'mytours-read-api-typescript'

import { ionHistory } from 'mobile_web/modules/navigation'
import strings from '../../../strings'

import './styles/screenPanorama.less'
import usePrevious from 'common_lib/hooks/use-previous'
import ImagePlaceholder from 'mobile_web/modules/common/components/placeholders/Image'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'
import ErrorMessageHandler from 'mobile_web/modules/common/components/error-message-handler'
import Panorama from './parts/Panorama'
import Modal from 'mobile_web/modules/common/components/modal'
import FullscreenMedia from '../components/parts/FullscreenMedia'
import FullscreenAr from './parts/FullscreenAr'
import { useLiveQuery } from 'dexie-react-hooks'
import db from 'mobile_web/modules/db'

interface OwnProps {
	isPageContent?: boolean
	loading: boolean
	isInsideModal?: boolean
}

type Props = Container.Props & Container.Actions & OwnProps

const ScreenPanorama: React.FC<Props> = props => {
	const [hotspots, setHotspots] = useState<Hotspot[] | undefined>()
	const [panoramaScreen, setPanoramaScreen] = useState<Screen>()
	const [currentScreenId, setCurrentScreenId] = useState<number>()
	const [hotspotsLoaded, setHotspotsLoaded] = useState<boolean>(false)

	const [arModalPresented, setArModalPresented] = useState<boolean>(false)
	const [glbUrl, setGlbUrl] = useState<string>()
	const [usdzUrl, setUsdzUrl] = useState<string>()
	const [arFilename, setArFilename] = useState<string>()

	const prevPropsItemId: number = usePrevious(props.itemId)
	const prevPropsScreen: Screen = usePrevious(props.screen)

	const [modalPresented, setModalPresented] = useState<boolean>(false)
	const [mediaItem, setMediaItem] = useState<MediaItem>(null)

	const earnedBadges = useLiveQuery(() => db.recentlyEarnedBadges.toArray())
	const trigger = useLiveQuery(
		() => db.triggers.where({ triggerable_id: props?.itemId, trigger_type: 'screen' }).first(),
		[props.itemId],
	)

	const onModalDidPresent = (): void => setModalPresented(true)

	const onModalDidDismiss = (): void => {
		setModalPresented(false)
		setMediaItem(null)
	}

	const loadItem = () => {
		const { itemId, itemVersion, language, accountId } = props

		if (!itemVersion) return

		props.loadItem(itemId, itemVersion, language, accountId)
	}

	useEffect(() => {
		loadItem()
	}, [])

	useEffect(() => {
		if (trigger && earnedBadges) {
			const earned = !!earnedBadges.find(b => b.badge_id === trigger.badge_id)
			if (!earned) {
				props.toggleBadgeModal(true, trigger.badge_id, true, true)
			}
		}
	}, [props.itemId, trigger, earnedBadges])

	useEffect(() => {
		if (!props.screen) return

		if (!currentScreenId) {
			setHotspotsLoaded(false)
			setCurrentScreenId(props.screen.id)
			setPanoramaScreen(props.screen)
		} else if (currentScreenId && !panoramaScreen) {
			const panoramaScreenNew = props.screens.find(({ id }) => id === currentScreenId)
			setHotspotsLoaded(false)

			if (panoramaScreenNew) {
				setPanoramaScreen(panoramaScreenNew)
			}
		} else if (
			currentScreenId &&
			panoramaScreen &&
			(prevPropsItemId !== props.itemId || (!prevPropsScreen && currentScreenId !== props.screen.id))
		) {
			setHotspotsLoaded(false)
			setCurrentScreenId(props.screen.id)
			setPanoramaScreen(undefined)
			setHotspots(undefined)
		}
	}, [currentScreenId, props.screen, panoramaScreen, props.itemId, props.screens])

	useEffect(() => {
		if (!props.screen || prevPropsItemId !== props.itemId) {
			setHotspotsLoaded(false)
			loadItem()
		}
	}, [props.screen, props.itemId, props.itemVersion])

	useEffect(() => {
		if (!hotspotsLoaded && panoramaScreen && currentScreenId === panoramaScreen.id) {
			const newHotspots = getHotspots()
			setHotspots(newHotspots)
		}
	}, [hotspotsLoaded, panoramaScreen, currentScreenId])

	const openModal = (sectionItem: SectionItemType): void => {
		const type = GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
		const subtype = sectionItem.item_subtype
			.split('_')
			.reverse()[0] as GetCollectionItemsIndexResponseData.ItemSubtypeEnum
		NavigationService.pushModalWindow(type, subtype, sectionItem.item_id)
	}

	const handleOpenAr = async (mediaItem: MediaItem) => {
		const { usdz, glb } = mediaItem
		setGlbUrl(glb?.original_url)
		setUsdzUrl(usdz?.original_url)
		setArFilename(glb?.filename || usdz?.filename)
	}

	const getHotspots = (): Hotspot[] | undefined => {
		if (!panoramaScreen?.sections || !Array.isArray(panoramaScreen?.sections)) {
			setHotspotsLoaded(true)
			return null
		}

		const hotSpots = []

		for (const section of panoramaScreen.sections) {
			section.section_items.forEach((sectionItem: SectionItemType) => {
				const {
					title,
					pitch,
					yaw,
					link_type: linkType,
					url,
					link_text: linkText,
					icon,
					item_version: itemVersion,
					icon_height: iconHeight,
					icon_width: iconWidth,
					x,
					y,
					id: hotspotId,
					name,
					item_type: itemType,
					item_subtype: itemSubtype,
					media_item: mediaItem,
				} = sectionItem
				const data = linksData[linkType] || {}

				const { subtype, id } = getSectionSubtypeAndId(sectionItem)

				const href = linkType !== 'internal' ? getSocialLink(url || linkText, data.link) : undefined

				const onClickAction =
					linkType === 'internal'
						? () => {
								if (subtype === ITEM_SUBTYPE_SCREEN_PANORAMA) {
									setHotspotsLoaded(false)
									setCurrentScreenId(id)
									setPanoramaScreen(undefined)
									setHotspots(undefined)
									props.loadItem(id, itemVersion, props.language, props.accountId)
								} else if ((itemSubtype as string) === 'media_item_ar') {
									handleOpenAr(mediaItem)
								} else if (itemType === GetScreensShowResponseSectionItems.ItemTypeEnum.MediaItem) {
									setMediaItem(mediaItem)
								} else {
									onLinkAction(subtype, id, itemVersion, () => openModal(sectionItem))
								}
						  }
						: undefined
				const hotSpot: Hotspot = {
					id: hotspotId,
					pitch,
					yaw,
					icon,
					iconHeight,
					iconWidth,
					x,
					y,
					text: getSocialLinkTitle(linkType, linkText || title || name),
					onClick: onClickAction,
				}

				if (href) {
					hotSpot.url = href
				}

				hotSpots.push(hotSpot)
			})
		}
		setHotspotsLoaded(true)
		return hotSpots
	}

	const onArModalDidPresent = (): void => setArModalPresented(true)

	const onArModalDidDismiss = (): void => {
		setArModalPresented(false)
		setUsdzUrl(undefined)
		setGlbUrl(undefined)
		setArFilename(undefined)
	}

	const renderPanorama = (): JSX.Element => {
		const { panorama = {}, image_type: imageType } = panoramaScreen || {}

		if (!panorama) {
			return null
		}

		const { file = {} as FileRec } = panorama

		if (!file.original_url) {
			return null
		}

		return <Panorama panoramaImage={panorama} imageType={imageType} hotspots={hotspots} />
	}

	const renderScreenContent = (): JSX.Element => {
		const { loading, error } = props

		return (
			<>
				<ErrorMessageHandler error={error} />
				{!loading && !error && panoramaScreen?.panorama && renderPanorama()}
				{!error && !loading && !hotspotsLoaded && !panoramaScreen?.panorama && (
					<ImagePlaceholder style={{ height: '100%' }} />
				)}
				{!error && !loading && hotspotsLoaded && !panoramaScreen?.panorama && (
					<div className="no-content-block">
						<IonText>{strings.no_content_available}</IonText>
					</div>
				)}
			</>
		)
	}

	const renderFullscreenMediaModal = (): JSX.Element => (
		<Modal
			isOpen={Boolean(mediaItem)}
			cssClass="media-modal"
			showBackdrop={true}
			onDidDismiss={onModalDidDismiss}
			onDidPresent={onModalDidPresent}
		>
			{modalPresented && (
				<FullscreenMedia
					mediaData={[mediaItem]}
					onBack={() => setMediaItem(null)}
					section={mediaItem}
					screen={props.screen}
				/>
			)}
		</Modal>
	)

	const renderArModal = (): JSX.Element => (
		<Modal
			isOpen={Boolean(glbUrl) || Boolean(usdzUrl)}
			cssClass="media-modal"
			showBackdrop={true}
			onDidDismiss={onArModalDidDismiss}
			onDidPresent={onArModalDidPresent}
		>
			{arModalPresented && (
				<FullscreenAr
					usdz={usdzUrl}
					glb={glbUrl}
					filename={arFilename}
					onClose={() => {
						setGlbUrl(undefined)
						setUsdzUrl(undefined)
					}}
				/>
			)}
		</Modal>
	)

	const { title } = panoramaScreen || {}

	const { pathname } = ionHistory.location

	const isPageWithoutTabs = pathname.includes(COLLECTION_ITEM_SUBTYPE_PANORAMA)

	const redirect = renderScreenRedirect(GetScreensShowResponse.ScreenTypeEnum.Panorama, props.screen?.screen_type)
	if (redirect) {
		return redirect
	}

	if (props.isPageContent) {
		return (
			<div
				data-item-id={String(props.itemId)}
				data-item-type="screen"
				data-item-subtype="story"
				style={{ width: '100%', height: '100%' }}
			>
				{renderScreenContent()}
			</div>
		)
	} else {
		return (
			<>
				<Helmet
					title={title}
					meta={[
						{ name: 'description', content: title }, // TODO: add description field to story
						{ property: 'og:type', content: 'website' }, // TODO: check type
						{ property: 'og:title', content: title },
						{ property: 'og:description', content: title }, // TODO: add description field to story
						{ property: 'og:url', content: window.location.href },
					]}
				/>
				<Page
					headerProps={{
						title: title || '',
						onBackButton: props.onBackAction,
						hideBackButton: props.isInsideModal,
						showBackButton: Boolean(props.onBackAction),
						type: 'short',

						collectionItem: null,
						itemType: GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen,
						itemId: panoramaScreen?.id,
						collection: null,
						screen: panoramaScreen,
						hideFavouriteButton: false,
					}}
					contentProps={{
						className: 'panorama-screen full-height',
					}}
					footer={{
						component: <AudioPlayerBarComponent bottomOffset={isPageWithoutTabs ? 0 : -1} />,
					}}
					dataItemId={String(props.itemId)}
					dataItemType="screen"
					dataItemSubtype="panorama"
					dataCy="page-panorama"
				>
					{renderScreenContent()}
					{mediaItem && renderFullscreenMediaModal()}
					{(glbUrl || usdzUrl) && renderArModal()}
				</Page>
			</>
		)
	}
}

const ScreenPanoramaComponent = Container.default(ScreenPanorama)
export default ScreenPanoramaComponent as any
